import React, { useCallback } from 'react';
import { Input } from '../UI/Input';
import { SelectInput } from '../UI/SelectInput';
import { Button } from '../UI/Button';
import type { OrderStatus } from '../../types/api';

export interface OrderFilterState {
  // 'closed' aggregate covers canceled+rejected+expired; the individual 'canceled'
  // OrderStatus is also a valid value (selectable below).
  status: OrderStatus | 'all' | 'open' | 'completed' | 'closed';
  symbol: string;
}

interface OrderFiltersProps {
  filters: OrderFilterState;
  onFilterChange: (filters: OrderFilterState) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open Orders' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed (Canceled / Rejected / Expired)' },
  // Individual statuses (wire format = lowercase snake_case)
  { value: 'pending_new', label: 'Pending New' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'new', label: 'New' },
  { value: 'partially_filled', label: 'Partially Filled' },
  { value: 'filled', label: 'Filled' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'pending_cancel', label: 'Pending Cancel' },
  { value: 'expired', label: 'Expired' },
  { value: 'pending_replace', label: 'Pending Replace' },
  { value: 'replaced', label: 'Replaced' },
];

/**
 * Order Filters component
 * Provides filtering and search for orders
 */
export function OrderFilters({
  filters,
  onFilterChange,
  onRefresh,
  isRefreshing,
}: OrderFiltersProps) {
  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterChange({
        ...filters,
        status: e.target.value as OrderFilterState['status'],
      });
    },
    [filters, onFilterChange]
  );

  const handleSymbolChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFilterChange({
        ...filters,
        symbol: e.target.value.toUpperCase(),
      });
    },
    [filters, onFilterChange]
  );

  const handleClearFilters = useCallback(() => {
    onFilterChange({
      status: 'all',
      symbol: '',
    });
  }, [onFilterChange]);

  const hasActiveFilters = filters.status !== 'all' || filters.symbol !== '';

  return (
    <div className="bg-white shadow rounded-lg p-4 mb-4">
      <div className="flex flex-wrap items-end gap-4">
        {/* Status Filter */}
        <div className="w-48">
          <SelectInput
            label="Status"
            name="status"
            value={filters.status}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS}
          />
        </div>

        {/* Symbol Search */}
        <div className="w-32">
          <Input
            label="Symbol"
            name="symbol"
            value={filters.symbol}
            onChange={handleSymbolChange}
            placeholder="AAPL"
            autoComplete="off"
          />
        </div>

        {/* Refresh Button */}
        <div className="flex items-center gap-2 ml-auto">
          {hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={handleClearFilters}
              className="text-sm"
            >
              Clear Filters
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="flex items-center"
          >
            <svg
              className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </Button>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 flex items-center flex-wrap gap-2">
          <span className="text-sm text-gray-500">Active Filters:</span>
          {filters.status !== 'all' && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {STATUS_OPTIONS.find((opt) => opt.value === filters.status)?.label}
              <button
                type="button"
                onClick={() => onFilterChange({ ...filters, status: 'all' })}
                className="ml-1 hover:text-blue-600"
              >
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </span>
          )}
          {filters.symbol && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Symbol: {filters.symbol}
              <button
                type="button"
                onClick={() => onFilterChange({ ...filters, symbol: '' })}
                className="ml-1 hover:text-blue-600"
              >
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
